<template>
  <div class="sp-translatable-field" :class="classModifiers" part="base">
    <div v-if="i18nReady" class="sp-translatable-field__toolbar" part="toolbar">
      <sp-menu
        class="sp-translatable-field__menu"
        location="bottom-right"
        :label="t('language_selector_label')"
        :open="languageMenuOpen"
        @update-open="onLanguageMenuOpen"
      >
        <sp-button
          slot="activator"
          class="sp-translatable-field__language"
          variant="outlined"
          color="surface"
          size="x-small"
          aria-haspopup="true"
          :aria-expanded="languageMenuOpen"
          :accessible-name="languageSelectorName"
          :data-testid="TestId.admin.translatableFieldLanguage"
        >
          <span class="sp-translatable-field__language-name">{{ selectedLanguageName }}</span>
          <sp-icon class="sp-translatable-field__language-caret" name="chevron-down" size="xx-small"></sp-icon>
        </sp-button>

        <ul class="sp-translatable-field__options" :aria-label="t('language_selector_label')">
          <li
            v-for="language in allLanguages"
            :key="language.locale"
            class="sp-translatable-field__option"
            :class="optionModifiers(language)"
          >
            <sp-button
              variant="text"
              color="text"
              size="small"
              block
              :pressed="language.locale === selectedLocale"
              :title="optionTitle(language)"
              :data-testid="TestId.admin.translatableFieldLanguageOption"
              :data-locale="language.locale"
              @click="selectLocale"
            >
              <sp-icon class="sp-translatable-field__option-check" name="check-alt-color" size="x-small"></sp-icon>
              <span class="sp-translatable-field__option-name">{{ language.optionName }}</span>
            </sp-button>
          </li>
        </ul>
      </sp-menu>

      <sp-menu
        v-if="offersClara"
        class="sp-translatable-field__menu"
        location="bottom-right"
        close-on-content-click="false"
        :label="pickerTitle"
        :open="pickerOpen"
        @update-open="onPickerOpen"
      >
        <sp-button
          slot="activator"
          class="sp-translatable-field__clara"
          variant="text"
          size="small"
          prepend-icon="sparkles"
          aria-haspopup="dialog"
          :aria-expanded="pickerOpen"
          :disabled="busy"
          :data-testid="TestId.admin.translatableFieldTranslate"
        >
          {{ claraLabel }}
        </sp-button>

        <form class="sp-translatable-field__picker" @submit.prevent="requestTranslations">
          <p class="sp-translatable-field__picker-title">{{ pickerTitle }}</p>
          <sp-checkbox
            class="sp-translatable-field__picker-all"
            :label="t('all_languages')"
            :checked.prop="allSelected"
            @update-checked="onToggleAll"
          ></sp-checkbox>
          <sp-checkbox
            v-for="language in languages"
            :key="language.locale"
            class="sp-translatable-field__picker-language"
            :label="language.name"
            :value="language.locale"
            :checked.prop="selectedLocales.includes(language.locale)"
            :data-testid="TestId.admin.translatableFieldTranslateLocale"
            :data-locale="language.locale"
            @update-checked="onToggleLocale"
          ></sp-checkbox>
          <div class="sp-translatable-field__picker-actions">
            <sp-button variant="text" size="small" @click="closePicker">{{ t("cancel") }}</sp-button>
            <sp-button
              type="submit"
              size="small"
              :disabled="submitDisabled"
              :data-testid="TestId.admin.translatableFieldTranslateSubmit"
            >
              {{ t("submit") }}
            </sp-button>
          </div>
        </form>
      </sp-menu>
    </div>

    <slot></slot>

    <p v-if="status.text" class="sp-translatable-field__status" :class="statusModifiers" role="status" part="status">
      {{ status.text }}
    </p>
  </div>
</template>

<script setup>
/**
 * Wraps a translatable attribute's base field and its per-locale fields (one light-DOM child per
 * `data-locale`) with a language selector that shows one of them at a time, and an action that
 * hands the languages an editor picks to Clara. The field also follows the page-wide language an
 * `sp-translation-toolbar` selects (see store/translation-page.js).
 *
 * @displayName Translatable Field
 * @group Custom Elements
 * @component sp-translatable-field
 *
 * @slot default - The base field and the per-locale fields, each carrying `data-locale`.
 */
import { useEventListener } from "@vueuse/core";
import { computed, onBeforeUnmount, onMounted, ref, useHost, watch } from "vue";

import { TestId } from "#/test-ids";
import { createApiClient } from "#/shared/api/client";
import { props as translatableFieldProps } from "./props.js";
import { t, whenI18nReady } from "./i18n";
import { useTranslationPageStore } from "./store/translation-page";

const ACTIVE_CLASS = "--is-active-locale";

const props = defineProps(translatableFieldProps);

const emit = defineEmits(["update-locale", "translations-requested"]);

const host = useHost();

const i18nReady = ref(false);

function markI18nReady() {
  i18nReady.value = true;
}

whenI18nReady.then(markI18nReady);

const languages = computed(() => {
  if (Array.isArray(props.languages)) {
    return props.languages;
  }

  try {
    const parsed = JSON.parse(props.languages || "[]");

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
});

// Bumped on every edit of a slotted field, so `hasText` re-reads the fields.
const fieldsVersion = ref(0);
// The controls an input or change event came from. Quill rewrites a textarea's default value along
// with its value, so an edit it announces is known by its event alone.
const editedControls = new WeakSet();

function noteFieldEdit(event) {
  if (event?.target instanceof Element) {
    editedControls.add(event.target);
  }

  fieldsVersion.value += 1;
}

useEventListener(host, "input", noteFieldEdit);
useEventListener(host, "change", noteFieldEdit);
onMounted(noteFieldEdit);

function localeFields() {
  return Array.from(host?.querySelectorAll(":scope > [data-locale]") ?? []);
}

function fieldFor(locale) {
  return localeFields().find((field) => field.dataset.locale === locale);
}

function hasText(locale) {
  const control = fieldFor(locale)?.querySelector("textarea, input:not([type=hidden])");

  return Boolean(control?.value?.trim());
}

// A per-locale control the editor left as rendered stays out of the submission, so a translation
// Clara wrote after the page rendered is neither blanked nor overwritten by the stale input. A field
// the server flags `data-translation-pending` was rendered from an unsaved edit and always submits.
function isSavedTranslationField(field) {
  return field.dataset.locale !== props.defaultLocale && !("translationPending" in field.dataset);
}

function translationControls() {
  return localeFields()
    .filter(isSavedTranslationField)
    .flatMap((field) => Array.from(field.querySelectorAll("textarea[name], input[name]:not([type=hidden])")));
}

function isUntouched(control) {
  return !editedControls.has(control) && control.value === control.defaultValue;
}

function dropUntouchedTranslations({ formData }) {
  translationControls()
    .filter(isUntouched)
    .forEach((control) => formData.delete(control.name));
}

const form = ref(null);

function findForm() {
  form.value = host?.closest("form") ?? null;
}

onMounted(findForm);
useEventListener(form, "formdata", dropUntouchedTranslations);

const defaultLanguageName = computed(() => props.defaultLanguageName ?? props.defaultLocale);

// Every translation is made from the base field's text, which the list says next to its name.
const allLanguages = computed(() => {
  fieldsVersion.value;

  const base = {
    locale: props.defaultLocale,
    name: defaultLanguageName.value,
    optionName: t("language_source", { language: defaultLanguageName.value }),
    hasText: hasText(props.defaultLocale),
  };
  const translated = languages.value.map((language) => ({
    ...language,
    optionName: language.name,
    hasText: hasText(language.locale),
  }));

  return [base, ...translated];
});

const anyTranslated = computed(() =>
  allLanguages.value.some((language) => language.locale !== props.defaultLocale && language.hasText),
);

const selectedLocale = ref(props.defaultLocale);

const page = useTranslationPageStore();

function knowsLocale(locale) {
  return locale === props.defaultLocale || languages.value.some((language) => language.locale === locale);
}

function followPageLocale() {
  if (knowsLocale(page.locale)) {
    selectedLocale.value = page.locale;
  }
}

watch(() => page.generation, followPageLocale);
onMounted(followPageLocale);

const unregisterField = page.registerField({
  attribute: props.attribute,
  label: props.label,
  languages,
});

onBeforeUnmount(unregisterField);

function optionModifiers(language) {
  return {
    "--is-selected": language.locale === selectedLocale.value,
    "--is-untranslated": !language.hasText,
  };
}

function optionTitle(language) {
  return language.hasText ? undefined : t("language_untranslated");
}

const selectedLanguageName = computed(
  () => allLanguages.value.find((language) => language.locale === selectedLocale.value)?.name ?? selectedLocale.value,
);

const languageSelectorName = computed(() => `${t("language_selector_label")}: ${selectedLanguageName.value}`);

function showSelectedField() {
  localeFields().forEach((field) =>
    field.classList.toggle(ACTIVE_CLASS, field.dataset.locale === selectedLocale.value),
  );
}

function announceLocale(locale) {
  showSelectedField();
  emit("update-locale", locale);
}

watch(selectedLocale, announceLocale);
onMounted(showSelectedField);

const languageMenuOpen = ref(false);

function onLanguageMenuOpen({ detail }) {
  const [open] = detail;
  languageMenuOpen.value = open;
}

function selectLocale({ currentTarget }) {
  selectedLocale.value = currentTarget.dataset.locale;
  languageMenuOpen.value = false;
}

const offersClara = computed(() => Boolean(props.translateUrl) && languages.value.length > 0);
const claraLabel = computed(() => (anyTranslated.value ? t("update") : t("generate")));
const pickerTitle = computed(() => t("picker_title", { language: defaultLanguageName.value }));

const pickerOpen = ref(false);
const selectedLocales = ref([]);

function resetPickerSelection() {
  selectedLocales.value = languages.value.map((language) => language.locale);
}

function onPickerOpen({ detail }) {
  const [open] = detail;
  pickerOpen.value = open;
  if (open) {
    resetPickerSelection();
  }
}

function closePicker() {
  pickerOpen.value = false;
}

const allSelected = computed(
  () => languages.value.length > 0 && selectedLocales.value.length === languages.value.length,
);

function onToggleAll({ detail }) {
  const [checked] = detail;
  selectedLocales.value = checked ? languages.value.map((language) => language.locale) : [];
}

function onToggleLocale({ currentTarget, detail }) {
  const { locale } = currentTarget.dataset;
  const [checked] = detail;
  const without = selectedLocales.value.filter((selected) => selected !== locale);
  selectedLocales.value = checked ? [...without, locale] : without;
}

const busy = ref(false);
const submitDisabled = computed(() => busy.value || selectedLocales.value.length === 0);
const status = ref({ text: "", error: false });

const api = createApiClient({ parse: false });

async function requestTranslations() {
  if (busy.value || selectedLocales.value.length === 0) {
    return;
  }

  const locales = [...selectedLocales.value];
  busy.value = true;
  status.value = { text: t("requesting"), error: false };

  try {
    const data = await api.post(props.translateUrl, {
      resource_type: props.resourceType,
      resource_id: props.resourceId,
      attribute_name: props.attribute,
      locales,
    });

    status.value = { text: data?.message ?? "", error: false };
    pickerOpen.value = false;
    emit("translations-requested", { locales, count: data?.count ?? 0 });
  } catch (error) {
    status.value = { text: error?.message || t("request_failed"), error: true };
  } finally {
    busy.value = false;
  }
}

const classModifiers = computed(() => ({
  "--offers-clara": offersClara.value,
  "--is-busy": busy.value,
}));

const statusModifiers = computed(() => ({ "--is-error": status.value.error }));
</script>

<style>
:host {
  display: block;
  position: relative;
  /* Shield from an ancestor's --sp-comp-menu-* popup tokens so the language and picker popups are
     sized by the field. */
  --sp-comp-menu-width: initial;
  --sp-comp-menu-min-width: initial;
  --sp-comp-menu-content-padding: initial;
}
</style>

<style lang="scss" scoped>
.sp-translatable-field {
  --toolbar-gap: var(--sp-comp-translatable-field-toolbar-gap, var(--sp-ref-scale-8));
  --toolbar-offset: var(--sp-comp-translatable-field-toolbar-offset, calc(-1 * var(--sp-ref-scale-4)));
  --toolbar-height: var(--sp-comp-translatable-field-toolbar-height, var(--sp-ref-scale-16));
  --menu-padding: var(--sp-comp-translatable-field-menu-padding, var(--sp-ref-scale-8));
  --menu-min-width: var(--sp-comp-translatable-field-menu-min-width, 12rem);
  --status-color: var(--sp-comp-translatable-field-status-color, var(--sp-sys-color-on-surface));
  --status-error-color: var(--sp-comp-translatable-field-status-error-color, var(--sp-sys-color-danger));

  position: relative;

  // The toolbar is as tall as the field's label line and centres its controls on it, raised by the
  // offset so a control taller than the line clears the input below.
  &__toolbar {
    position: absolute;
    top: var(--toolbar-offset);
    right: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: var(--toolbar-gap);
    height: var(--toolbar-height);
  }

  // sp-menu is a full-width block by default; in the toolbar it takes its activator's width.
  // The popups grow to their content: the language list to its longest name, the picker to its title.
  &__menu {
    --sp-comp-menu-min-width: var(--menu-min-width);
    --sp-comp-menu-width: max-content;

    flex: 0 0 auto;
    width: auto;
  }

  &__language,
  &__clara {
    white-space: nowrap;
  }

  // The selector is a pill: a rounded, hairline-bordered surface with the language and a caret.
  &__language {
    --sp-comp-button-x-small-height: var(--sp-ref-scale-20);
    --sp-comp-button-border-radius: var(--sp-ref-border-radius-full);
    --sp-comp-button-outlined-border-color: var(--sp-sys-color-outline-variant);
    --sp-comp-button-hover-outlined-border-color: var(--sp-sys-color-outline);
    --sp-comp-button-surface-outlined-text-color: var(--sp-sys-color-on-surface);
    --sp-comp-button-surface-hover-outlined-bg-color: var(--sp-sys-color-surface);
    --sp-comp-button-x-small-font-size: var(--sp-ref-font-size-small);
    --sp-comp-button-x-small-padding: 0 var(--sp-ref-scale-6) 0 var(--sp-ref-scale-12);
    --sp-comp-button-font-weight: var(--sp-ref-font-weight-normal);
  }

  &__language-name {
    margin-inline-end: var(--sp-ref-scale-4);
  }

  &__language-caret {
    vertical-align: middle;
  }

  // The selected language carries a check; a language with no text yet is muted.
  &__option sp-button {
    --sp-comp-button-justify-content: flex-start;
    --sp-comp-button-content-text-align: start;
  }

  &__option-check {
    margin-inline-end: var(--sp-ref-scale-6);
    visibility: hidden;
  }

  &__option.--is-selected &__option-check {
    visibility: visible;
  }

  &__option.--is-untranslated &__option-name {
    opacity: 0.55;
  }

  &__options {
    box-sizing: border-box;
    min-width: var(--menu-min-width);
    margin: 0;
    padding: var(--menu-padding);
    list-style: none;
  }

  &__option-name {
    flex: 1 1 auto;
    text-align: start;
  }

  &__picker {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--sp-ref-scale-8);
    min-width: var(--menu-min-width);
    padding: var(--menu-padding);
  }

  &__picker-title {
    margin: 0;
    font-weight: var(--sp-ref-font-weight-medium);
    white-space: nowrap;
  }

  &__picker-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--sp-ref-scale-8);
    margin-block-start: var(--sp-ref-scale-8);
  }

  &__status {
    margin: var(--sp-ref-scale-8) 0 0;
    color: var(--status-color);
    font-size: var(--sp-ref-font-size-small);

    &.--is-error {
      color: var(--status-error-color);
    }
  }
}
</style>
